'use client';
import { create } from "zustand";
import { immer } from 'zustand/middleware/immer'
import SalesEnum from "@/app/enums/Health/SalesEnum";
import ISale from "@/@types/health/sale";
import { getCookie, removeCookie, setCookie, setCrossDomainCookie } from "@/app/utils/client-cookies";
import { Products } from "@/app/enums/Products";
import { getRandomValueFromDistribution } from "@/app/utils/probability";

export const HEALTH_EXPERT_ID_COOKIE = 'rm_health_expert_id';

const BASE_API = process.env.NEXT_PUBLIC_RM_BASE_API + '/api';
// const BASE_API = 'http://dev.reassurez-moi.fr/api';
const BASE_WEBSITE = process.env.NEXT_PUBLIC_RM_BASE_API;

const loadSale = (forcedSaleId?: number) => {
    const sales = SalesEnum.reviews
    let currentSaleId: ISale["expert_id"] = forcedSaleId || getSaleFromCookie();

    if (!currentSaleId) {
        currentSaleId = getRandomSaleId();
        if (currentSaleId) setCrossDomainCookie(HEALTH_EXPERT_ID_COOKIE, currentSaleId.toString())
    }

    const matchedCurrentSale = sales.find(sale => sale.expert_id === parseInt(currentSaleId as string))
    if (matchedCurrentSale) {
        return matchedCurrentSale
    }
}

const getSaleFromCookie = () => {
    const sales = SalesEnum.reviews
    let expertIdCookie = getCookie(HEALTH_EXPERT_ID_COOKIE) as string | undefined;

    if (expertIdCookie && !sales.find(sale => sale.expert_id === parseInt(expertIdCookie as string))) {
        expertIdCookie = undefined;
    }

    return expertIdCookie;
}

const getRandomSaleId = () => {
    const email = getRandomValueFromDistribution(JSON.parse(process.env.NEXT_PUBLIC_HEALTH_SALE_DISTRIBUTION as string), 'email');
    const saleId = SalesEnum.ID_MAP_BY_MAIL[email] || SalesEnum.SALE_HEALTH.expert_id
    return saleId || SalesEnum.SALE_HEALTH.expert_id
}

const setupOriginRoute = () => {
    if (!getCookie('health_origin_route')) {
        setCookie('health_origin_route', 'comparateur-mutuelle-sante')

        return 'comparateur-mutuelle-sante'
    }

    return getCookie('health_origin_route')
}

export interface HealthLandingStoreState {
    token?: string;
    currentQuote: {};
    isInitialLoading: boolean;
    callbackForm: {
        lastName?: string;
        firstName?: string;
        email?: string;
        phone?: string;
        date?: string;
        hours?: number;
        acceptPhoneContact?: boolean;
        acceptEmailContact?: boolean;
        legalConsent?: boolean;
        // kill switch: when false, the callback payload carries no consent
        phoneConsentMode?: boolean;
        zipcode?: string;
        city?: string;
    },
    landingForm: {
        familyType: number | null;
    },
    isCallbackFormLoading: boolean,
    currentSale: ISale,
    setCurrentSale: Function,
    setProperty: Function,
    setToken: Function,
    loadQuote: Function,
    validateLanding: Function,
    sendValidateLandingRequest: Function,
    getUrls: Function,
    askForAppointment: Function,
    createQuote: Function,
    setQuote: Function,
    loadSale: Function,
    originRoute: string,
}

export const useHealthLandingStore = create<HealthLandingStoreState>()(
    immer((set, get) => {
        return {
            token: undefined,
            isInitialLoading: true,
            landingForm: {
                familyType: null,
            },
            callbackForm: {
                lastName: undefined,
                firstName: undefined,
                email: undefined,
                phone: undefined,
                date: new Date().toISOString().split('T')[0],
                hours: 1000,
                acceptPhoneContact: false,
                acceptEmailContact: false,
                legalConsent: false,
                phoneConsentMode: true,
                zipcode: undefined,
                city: undefined,
            },
            isCallbackFormLoading: false,
            currentQuote: {},
            currentSale: SalesEnum.SALE_HEALTH,
            originRoute: setupOriginRoute(),

            createQuote: async (askForAppointment = false) => {
                const { currentSale, token, callbackForm } = get();

                const formData = {
                    token: token as string,
                    abTestReviewsVersion: '',
                    assignToExpert: currentSale.expert_id?.toString() || '',
                    partnerSource: '',
                    email: callbackForm.email as string,
                }

                set(state => {
                    state.isCallbackFormLoading = true
                })

                return fetch(`${BASE_API}/health/validate-landing`, {
                    body: JSON.stringify(formData),
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                })
                    .then(async (response) => {
                        const data = await response.json()
                        set(state => {
                            state.token = data.token
                        })

                        if (askForAppointment) {
                            return get().askForAppointment()
                        }
                    })
                    .finally(() => {
                        set(state => {
                            state.isCallbackFormLoading = false
                        })
                    })
            },
            askForAppointment: async () => {
                const { callbackForm, token } = get();

                const phoneToPlus33 = callbackForm.phone?.replace(/^0/, '+33')

                const formData = {
                    token: token as string,
                    appointment_date: new Date(callbackForm.date as string),
                    appointment_time: (callbackForm.hours || '').toString(),
                    first_name: callbackForm.firstName as string,
                    last_name: callbackForm.lastName as string,
                    phone: phoneToPlus33 as string,
                    email: callbackForm.email as string,
                    product_type: Products.HEALTH,
                    type: 11,
                    from_link: window.location.href,
                    zipcode: callbackForm.zipcode as string,
                    city: callbackForm.city as string,
                    // Consent mode off: nothing was collected, so no consent is written
                    ...(callbackForm.phoneConsentMode === false ? {} : {
                        accept_phone_contact: callbackForm.acceptPhoneContact === true,
                        accept_email_contact: callbackForm.acceptEmailContact === true,
                    }),
                }

                set(state => {
                    state.isCallbackFormLoading = true
                })

                await fetch(`${BASE_API}/health/callback`, {
                    body: JSON.stringify(formData),
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                })

                const response = await fetch(`${BASE_API}/common/callback`, {
                    body: JSON.stringify(formData),
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                })

                set(state => {
                    state.isCallbackFormLoading = false
                })

                return response.status === 200
            },
            validateLanding: (familyType: string | null = null) => {
                set(state => {
                    state.landingForm.familyType = familyType !== null ? parseInt(familyType) : null
                })

                const { currentSale, token, sendValidateLandingRequest, originRoute } = get();
                const formData = {
                    abTestReviewsVersion: null,
                    token,
                    assignToExpert: currentSale.expert_id,
                    partnerSource: null,
                    originRoute: originRoute,
                }
                const storedTokenInCookie = getCookie('health_quote_stored_token')
                const isUserCRM = window && window.role && window.role.includes('ROLE_MUTUELLE_SANTE')

                if (!token && storedTokenInCookie && !isUserCRM) {
                    fetch(`${BASE_API}/health/check-token-from-cookie/${storedTokenInCookie}`)
                        .then(async (response) => {
                            const data = await response.json()
                            if (data) {
                                sendValidateLandingRequest({ ...formData, token: data })
                            } else {
                                removeCookie(HEALTH_EXPERT_ID_COOKIE)
                                sendValidateLandingRequest({ ...formData, token: null })
                            }
                        })
                        .catch(() => {
                            removeCookie(HEALTH_EXPERT_ID_COOKIE)
                            sendValidateLandingRequest(formData)
                        })
                } else {
                    sendValidateLandingRequest(formData)
                }
            },
            getUrls: (token?: string) => ({
                comparatorForm: `${BASE_WEBSITE}/devis-mutuelle-sante/${token}/1?familyType=${get().landingForm.familyType}&comesFromLanding=true`,
            }),
            sendValidateLandingRequest: (formData: any) => {
                fetch(`${BASE_API}/health/validate-landing`, {
                    method: 'POST',
                    body: JSON.stringify(formData),
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                })
                    .then(async (response) => {
                        const data = await response.json()
                        get().setToken(data.token)
                        setCrossDomainCookie('health_quote_stored_token', data.token)
                        window.history.pushState({}, '', `/comparateur-mutuelle-sante/${data.token}`)
                        const url = get().getUrls(data.token).comparatorForm

                        window.location.href = url
                    })
                    .catch(() => {
                        // this.loading = false
                        // this.$toasted.error('Une erreur est survenue, veuillez réessayer plus tard')
                    })
                    .finally(() => {
                        // this.loading = false
                    })

            },
            setProperty: (property: keyof HealthLandingStoreState['callbackForm'], value: any) => set((state) => { state.callbackForm[property] = value }),
            setCurrentSale: (sale: ISale) => set((state) => { state.currentSale = sale }),
            setToken: (token: string) => set((state) => { state.token = token }),
            loadQuote: (token: string) => {
                set((state) => { state.token = token; state.isInitialLoading = true })

                fetch(`${BASE_API}/health/token/${token}`)
                    .then(async (response) => {

                    })
                    .catch((er) => {
                    })
                    .finally(() => {
                        set((state) => { state.isInitialLoading = false; })
                    })
            },
            setQuote: (quote: any) => {
                const data = quote
                const pi = data.main_contractor?.prospect_info
                set((state) => {
                    state.currentSale = loadSale(data.sales_person?.id) as ISale
                    state.currentQuote = data
                    state.callbackForm.firstName = pi?.first_name
                    state.callbackForm.lastName = pi?.last_name
                    state.callbackForm.zipcode = pi?.address_zip_code
                    state.callbackForm.city = pi?.address_city
                    // phone number from 0643443515 to 06 43 44 35 15
                    state.callbackForm.phone = pi?.phone?.length > 1 ? pi.phone.replace('+33', '0').replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5') : pi.phone
                    state.callbackForm.email = pi?.prospect?.email
                })
            },
            loadSale,
        } as HealthLandingStoreState
    })
)
